// Individual analytics endpoints cap legacy responses at 1,000 records,
// independently of the aggregated grid's page_size. Never infer completeness
// from that grid's 100-row default or compare only the capped first page.
export const collectIndividualExportRows = async (payload, fetchWindow, isCancelled = () => false) => {
  let requests = 0
  let firstRows
  const incomplete = message => { throw Object.assign(new Error(message), { code: "INCOMPLETE" }) }
  const collect = async (start, end, depth = 0) => {
    if (isCancelled()) throw new Error("Validation stopped")
    if (++requests > 127) incomplete("The selected range needs too many page requests to verify export completeness.")
    const response = await fetchWindow({ ...payload, start_date: start, end_date: end })
    const rows = Array.isArray(response) ? response : response?.data
    if (!Array.isArray(rows)) incomplete("The individual analytics API did not return a row collection.")
    if (depth === 0) firstRows = rows
    const lo = Date.parse(start), hi = Date.parse(end)
    if (depth > 0 && rows.some(row => {
      const time = Date.parse(row.timestamp)
      return !Number.isFinite(time) || time < lo || time > hi
    })) incomplete("The production page API caps individual results and does not honor sub-day time bounds. Full-day export parity requires a pageable reference API; only the available page sample can be checked.")
    if (response?.additional_data?.has_next_page === true)
      incomplete("Individual analytics returned pagination that this export contract does not support.")
    if (rows.length < 1000) return rows
    if (payload.individual_clicks && rows.every(row => "pst_day" in row && "click_time" in row))
      incomplete("The individual Clicks reference is capped and uses reporting-day bounds; complete filtered partitions are required.")
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi - lo < 2 || depth >= 12)
      incomplete("Individual analytics is still capped in the smallest validation window.")
    const mid = Math.floor((lo + hi) / 2)
    const boundary = new Date(mid).toISOString()
    const left = await collect(start, boundary, depth + 1)
    const right = await collect(boundary, end, depth + 1)
    // Both API bounds are inclusive. Keep the shared boundary only on the
    // right, using the returned event timestamp rather than deduplicating IDs.
    if (left.some(row => !Number.isFinite(Date.parse(row.timestamp))))
      incomplete("Missing event timestamps prevent safe combination of capped analytics windows.")
    return [...left.filter(row => Date.parse(row.timestamp) < mid), ...right]
  }
  try {
    return { response: { data: await collect(payload.start_date, payload.end_date) }, complete: true }
  } catch (error) {
    if (error.code !== "INCOMPLETE" || !firstRows) throw error
    return { response: { data: firstRows }, complete: false, reason: error.message }
  }
}

// Enumerate disjoint partitions from the complete aggregate API, never from
// the capped sample or from the CSV being validated. Verify each partition's
// cardinality and membership before declaring the reference complete.
export const collectPartitionedClickRows = async (payload, fetchGroups, fetchRows, isCancelled = () => false) => {
  const dimensions = [["state", "states"], ["campaign_id", "campaign_ids"], ["source_type_id", "source_type_ids"]]
  let requests = 0
  const shortfalls = []
  const fail = message => { throw Object.assign(new Error(message), { code: "INCOMPLETE" }) }
  const check = () => {
    if (isCancelled()) throw new Error("Validation stopped")
    if (++requests > 100) fail("Clicks reference partitioning exceeded the validation request limit.")
  }
  const collect = async (filters, depth, expected) => {
    if (depth >= dimensions.length) fail("A Clicks reference partition still exceeds the API cap.")
    const [dimension, filter] = dimensions[depth]
    check()
    const grouped = await fetchGroups({ ...payload, ...filters, individual_clicks: false, group_by_params: [dimension] })
    if (!grouped.complete) fail("The Clicks partition inventory is capped; full reference coverage cannot be established.")
    const groups = grouped.response?.data
    if (!Array.isArray(groups)) fail("The Clicks partition inventory is missing.")
    const seen = new Set()
    let total = 0
    for (const group of groups) {
      const rawCount = group.click_analytic_clicks
      const count = Number(rawCount)
      if (rawCount == null || rawCount === "" || !Number.isSafeInteger(count) || count < 0)
        fail("The Clicks partition inventory has no valid click count.")
      if (!count) continue
      const value = group[dimension]
      if (value == null || String(value).trim() === "" || seen.has(String(value)))
        fail("The Clicks partition inventory has missing or ambiguous dimension values.")
      seen.add(String(value))
      total += count
    }
    if (expected !== undefined && total !== expected)
      fail("Grouped Clicks counts disagree across partition levels.")
    const result = []
    for (const group of groups) {
      const count = Number(group.click_analytic_clicks)
      if (!count) continue
      const value = group[dimension]
      const partition = { ...filters, [filter]: [value] }
      let rows
      if (count >= 1000) rows = await collect(partition, depth + 1, count)
      else {
        check()
        const response = await fetchRows({ ...payload, ...partition })
        rows = Array.isArray(response) ? response : response?.data
      }
      if (!Array.isArray(rows) || rows.length >= 1000 && count < 1000)
        fail(`Clicks reference is unexpectedly capped for ${dimension}=${value}.`)
      if (rows.length !== count)
        shortfalls.push(`${dimension}=${value}: expected ${count}, returned ${rows.length}`)
      if (rows.some(row => Object.entries(partition).some(([key, values]) => {
        const field = dimensions.find(([, name]) => name === key)[0]
        return String(row[field]) !== String(values[0])
      }))) fail("The Clicks page API did not honor the partition filters.")
      result.push(...rows)
    }
    return result
  }
  const rows = await collect({}, 0)
  const ids = rows.map(row => row.click_id)
  if (ids.some(id => id == null || String(id).trim() === "") || new Set(ids.map(String)).size !== ids.length)
    fail("The combined Clicks reference has missing or duplicate Click IDs.")
  if (shortfalls.length) throw Object.assign(new Error(
    `The production individual Clicks API disagrees with its complete grouped inventory (${shortfalls.join("; ")}). All available partition rows were checked; full parity cannot be certified.`
  ), { code: "INCOMPLETE", response: { data: rows } })
  return { response: { data: rows }, complete: true }
}
